import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PRESS_ARTICLES } from "@/data/content";
import { Reveal, ChapterLabel, ImageReveal, EASE } from "@/components/Shared";

const pagesOf = (a) => a.pages || (a.img ? [a.img] : []);

const ScanLightbox = ({ item, start, onClose }) => {
  const { t } = useLang();
  const pages = pagesOf(item);
  const [idx, setIdx] = useState(start);
  const prev = () => setIdx((i) => (i - 1 + pages.length) % pages.length);
  const next = () => setIdx((i) => (i + 1) % pages.length);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pages.length, onClose]);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[95] overflow-y-auto bg-[#0B0B0C]/95 p-6 backdrop-blur-sm sm:p-12"
      onClick={onClose}
      data-testid="press-scan-lightbox"
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={idx}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          src={pages[idx]}
          alt={`${item.publication} ${idx + 1}`}
          data-testid="press-scan-image"
          onClick={(e) => { e.stopPropagation(); next(); }}
          className="mx-auto max-w-4xl cursor-pointer shadow-[0_60px_120px_-30px_rgba(0,0,0,0.9)]"
        />
      </AnimatePresence>
      {pages.length > 1 && (
        <div className="fixed bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-6" onClick={(e) => e.stopPropagation()}>
          <button data-testid="press-scan-prev" onClick={prev} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#C5A059]/50 text-[#C5A059] transition-colors hover:bg-[#C5A059] hover:text-[#0B0B0C]">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span data-testid="press-scan-counter" className="font-mono text-[10px] tracking-[0.3em] text-[#A1A1AA]">
            {t.press.pageLabel} {idx + 1} / {pages.length}
          </span>
          <button data-testid="press-scan-next" onClick={next} className="flex h-11 w-11 items-center justify-center rounded-full border border-[#C5A059]/50 text-[#C5A059] transition-colors hover:bg-[#C5A059] hover:text-[#0B0B0C]">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
      <button
        data-testid="press-scan-close-button"
        onClick={onClose}
        className="fixed right-8 top-8 font-mono text-xs tracking-[0.3em] text-[#A1A1AA] transition-colors hover:text-[#C5A059]"
      >
        CLOSE
      </button>
    </motion.div>
  );
};

const Scans = ({ a, pub, onOpen }) => {
  const { t } = useLang();
  const pages = pagesOf(a);
  if (!pages.length) {
    return (
      <div className="flex aspect-[3/4] w-full items-center justify-center border border-white/10 bg-[#141416]">
        <Newspaper className="h-10 w-10 text-[#C5A059]/40" strokeWidth={1} />
      </div>
    );
  }
  return (
    <div>
      <button
        type="button"
        data-testid={`press-article-scan-${a.id}`}
        data-cursor="VIEW"
        onClick={() => onOpen(a, 0)}
        className="group/scan block w-full text-left"
      >
        <div className="-rotate-1 shadow-[0_60px_100px_-40px_rgba(0,0,0,0.9)] transition-transform duration-700 group-hover/scan:rotate-0">
          <ImageReveal src={pages[0]} alt={pub} className={`${a.landscape ? "aspect-[16/10]" : "aspect-[3/4]"} w-full`} imgClass="object-cover object-top" />
        </div>
      </button>
      {pages.length > 1 && (
        <div className="mt-4 grid grid-cols-3 gap-3">
          {pages.map((pg, i) => (
            <button
              key={pg}
              type="button"
              data-testid={`press-article-page-${a.id}-${i + 1}`}
              data-cursor="VIEW"
              onClick={() => onOpen(a, i)}
              className="group/pg relative aspect-[3/4] overflow-hidden border border-white/10 transition-colors hover:border-[#C5A059]/60"
            >
              <img src={pg} alt={`${pub} ${i + 1}`} loading="lazy" className="h-full w-full object-cover object-top opacity-70 transition-all duration-500 group-hover/pg:scale-105 group-hover/pg:opacity-100" />
              <span className="absolute bottom-2 left-2 font-mono text-[9px] tracking-[0.25em] text-[#F9F8F6]">0{i + 1}</span>
            </button>
          ))}
        </div>
      )}
      <span className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">
        {pages.length > 1 ? t.press.pagesHint : t.press.viewScan} <ArrowUpRight className="h-3 w-3" />
      </span>
    </div>
  );
};

const Article = ({ a, index, onOpen }) => {
  const { lf } = useLang();
  const pub = typeof a.publication === "string" ? a.publication : lf(a.publication);
  const sections = a.full ? lf(a.full) : [{ h: null, p: lf(a.text) }];
  return (
    <article data-testid={`press-article-${a.id}`} className="grid gap-10 border-t border-white/5 py-16 lg:grid-cols-12 lg:gap-16 sm:py-20">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-32">
          <Reveal><Scans a={a} pub={pub} onOpen={onOpen} /></Reveal>
        </div>
      </div>

      <div className="lg:col-span-7 lg:col-start-6">
        <Reveal>
          <div className="flex items-baseline gap-5">
            <span className="font-serif text-5xl font-light text-outline-gold sm:text-6xl">{String(index + 1).padStart(2, "0")}</span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">{pub} · {lf(a.issue)}</p>
              <h3 className="mt-2 font-serif text-3xl font-light leading-tight text-[#F9F8F6] sm:text-4xl">{lf(a.title)}</h3>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 font-serif text-2xl font-light italic leading-relaxed text-[#F9F8F6]/90 sm:text-3xl">„{lf(a.quote)}“</p>
        </Reveal>
        <div className="mt-8 space-y-8">
          {sections.map((sec, si) => (
            <Reveal key={si} delay={0.1}>
              {sec.h && <h4 className="mb-4 font-serif text-xl font-light text-[#C5A059]">{sec.h}</h4>}
              <div className="space-y-5">
                {sec.p.map((para, i) => (
                  <p key={i} className="max-w-2xl font-sans text-sm leading-loose text-[#A1A1AA]">{para}</p>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
        {a.author && (
          <Reveal delay={0.2}>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">— {a.author}, {pub}</p>
          </Reveal>
        )}
      </div>
    </article>
  );
};

export const PressArticles = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(null);
  const onOpen = (a, page) => setOpen({ a, page });
  return (
    <section data-testid="press-articles-section" className="border-t border-white/5 px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
      <Reveal><ChapterLabel no="03" label={t.press.articlesLabel} /></Reveal>
      <Reveal delay={0.1}>
        <p className="mt-6 max-w-xl font-sans text-sm leading-loose text-[#A1A1AA]">{t.press.articlesIntro}</p>
      </Reveal>
      <div className="mt-12">
        {PRESS_ARTICLES.map((a, i) => (
          <Article key={a.id} a={a} index={i} onOpen={onOpen} />
        ))}
      </div>
      <AnimatePresence>
        {open && <ScanLightbox item={open.a} start={open.page} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </section>
  );
};
